import { Injectable, Injector, OnDestroy, Type } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { Web3WalletSignatureInfo, Web3WalletType } from '../../interfaces/auth.interface';
import { NetworksInterface } from '../../interfaces/deposit.interface';
import { Web3WalletAdapter } from '../../interfaces/web3-wallet.interface';
import { LocalStorageService } from '../localstorage.service';

/**
 * 錢包 adapter 載入器。
 *
 * **一律使用動態 import，不要改回 static import。**
 * 四家 adapter 各自獨佔一套重量級 SDK，彼此不重疊：
 *
 * | adapter       | 獨佔 SDK                                        | ethers |
 * |---------------|------------------------------------------------|--------|
 * | MetaMask      | `@metamask/connect-evm`                        | ✅     |
 * | Coinbase      | `@coinbase/wallet-sdk`                         | ✅     |
 * | TonConnect    | `@orbs-network/ton-access`、`@ton/ton`、`@tonconnect/ui` | ❌ 不用 |
 * | WalletConnect | `@reown/appkit`、`appkit-adapter-ethers`、`viem` | ✅     |
 *
 * 靜態 import 會讓「任何碰到 Web3WalletService 的 chunk」一次背走全部四套；
 * 改為動態後，使用者只下載自己選的那一家（`ethers` 由三家共用，會被提為共用 chunk）。
 *
 * 這條路徑目前仍會被匿名首頁觸及（`home.module` → `ThirdAuthM8Component` →
 * `ThirdAuthM8Service` → 本服務），所以拆分的收益直接落在首屏，
 * 而不必為此把 `HomeComponent` 改成 standalone 再上 `@defer`。
 *
 * 注意：本專案用的是 esbuild builder（`angular.json` 的 `:application`），
 * `webpackChunkName` 一類的 magic comment 無效，chunk 檔名是雜湊、無法指定。
 * 要對應回來源請看 `npm run analyzer` 產出的 stats。
 */
const ADAPTER_LOADERS: Partial<Record<Web3WalletType, () => Promise<Type<Web3WalletAdapter>>>> = {
  [Web3WalletType.MetaMask]: () => import('./metamask.service').then(m => m.MetaMaskService),
  [Web3WalletType.CoinbaseWallet]: () => import('./coinbase-wallet.service').then(m => m.CoinbaseWalletService),
  [Web3WalletType.TonConnect]: () => import('./ton-connect.service').then(m => m.TonConnectService),
  [Web3WalletType.WalletConnect]: () => import('./wallet-connect.service').then(m => m.WalletConnectService),
};

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class Web3WalletService implements Web3WalletAdapter, OnDestroy {
  constructor(
    private injector: Injector,
    private localStorageService: LocalStorageService,
  ) {
    // 恢復 Web3 當前連線(處理因 login/logout 而 window.reBuild 導致 angular 重新載入問題)
    //
    // adapter 改為動態載入後，此處多了「chunk 下載失敗」這個新的失敗模式
    // （佈署後舊分頁拿到失效檔名、或網路中斷）。建構式無法 await，
    // 未捕捉會成為 unhandled rejection。還原失敗時維持未連線狀態，
    // 使用者重新點一次錢包即可，不阻斷其他功能。
    void this.checkExistingConnection().catch(() => undefined);
  }

  private _currentWalletService: Web3WalletAdapter | null = null;
  private readonly _destroyCurrSubscription$ = new Subject<void>();
  /**
   * 已載入並實例化的 adapter。
   *
   * 這不是為了取代 Injector 的快取（`providedIn:'root'` 本來就只建構一次），
   * 而是為了兩件事：省去重複 await 動態 import（即使命中 module registry
   * 仍是一次 microtask），以及讓 `disconnectWallet` 能**同步**知道碰過誰 ——
   * 否則為了斷線得把四家全部載入並實例化，等於重新觸發
   * `createAppKit()` 與 `new TonConnectUI()`（後者會去抓 config.ton.org
   * 的錢包清單，約 564 KB）。
   */
  private readonly _loadedAdapters = new Map<Web3WalletType, Web3WalletAdapter>();

  /**
   * 載入並取得指定 adapter，其 SDK 於此刻才下載。
   * @returns 無對應 adapter 時回傳 null；呼叫端 `_checkWalletServiceExisting()` 會轉為錯誤
   */
  private async _adapter(walletType: Web3WalletType): Promise<Web3WalletAdapter | null> {
    let adapter = this._loadedAdapters.get(walletType);

    if (!adapter) {
      const token = await ADAPTER_LOADERS[walletType]?.();
      if (!token) return null;

      adapter = this.injector.get(token);
      this._loadedAdapters.set(walletType, adapter);
    }

    // adapter 的建構式可能有非同步暖身（見 Web3WalletAdapter.ready）。
    // 靜態注入時代它在 bootstrap 就開始跑，使用者點擊時早已就緒；
    // 改為點擊當下才實例化後，暖身與首次呼叫之間沒有空檔，必須顯式等待。
    // 未定義 ready 的 adapter 會直接通過（await undefined 立即 resolve）。
    // 放在快取判斷之外，是為了讓快取命中的路徑也等 —— 連續快速點擊時，
    // 第二次呼叫拿到的是同一個尚未暖身完成的實例。
    await adapter.ready;

    return adapter;
  }

  private readonly _isConnected$ = new BehaviorSubject<boolean>(false);
  private readonly _walletType$ = new BehaviorSubject<Web3WalletType | null>(null);
  private readonly _accounts$ = new BehaviorSubject<string[]>([]);
  private readonly _currentChainId$ = new BehaviorSubject<number | string | null>(null);

  readonly isConnected$: Observable<boolean> = this._isConnected$.asObservable();
  readonly walletType$: Observable<Web3WalletType | null> = this._walletType$.asObservable();
  readonly accounts$: Observable<string[]> = this._accounts$.asObservable();
  readonly currentChainId$: Observable<number | string | null> = this._currentChainId$.asObservable();

  ngOnDestroy(): void {
    this._destroyCurrSubscription$.next();
    this._destroyCurrSubscription$.complete();
  }

  /**
   * 重置狀態
   */
  private _resetWalletState(): void {
    // Remove the wallet type from localStorage
    this.localStorageService.clearConnectedWeb3WalletType();

    this._isConnected$.next(false);
    this._walletType$.next(null);
    this._accounts$.next([]);
    this._currentChainId$.next(null);
    this._currentWalletService = null;
  }

  /**
   * 確認 Web3WalletType Service 是否存在
   */
  private _checkWalletServiceExisting(): void {
    if (!this._currentWalletService) {
      throw new Error('No web3 wallet service available');
    }
  }

  /**
   * 初始化 Web3WalletType Service 與相關訂閱設置
   * @param walletType
   */
  private async _walletServiceInit(walletType: Web3WalletType): Promise<void> {
    try {
      // 清理之前的訂閱
      if (this._currentWalletService || this._walletType$.getValue()) {
        this._destroyCurrSubscription$.next();
        this._resetWalletState();
      }

      // Store the wallet type in localStorage
      this.localStorageService.setConnectedWeb3WalletType(walletType);

      // 選擇對應的錢包服務（此處才下載該 adapter 的 chunk 並實例化）。
      // 兩個呼叫端 checkExistingConnection / connectWallet 本來就 await 本方法，
      // 因此這個 await 不會往外擴散。
      this._currentWalletService = await this._adapter(walletType);
      this._checkWalletServiceExisting();

      // 設置新的連線類型
      this._walletType$.next(walletType);

      // 設置新的訂閱
      this._setupWalletStateSubscription();
    } catch (error) {
      this._destroyCurrSubscription$.next();
      this._resetWalletState();
      throw error;
    }
  }

  /**
   * 訂閱 Web3WalletType Service 的狀態
   */
  private _setupWalletStateSubscription(): void {
    if (!this._currentWalletService) return;

    // 訂閱連接狀態
    this._currentWalletService.isConnected$
      .pipe(untilDestroyed(this), takeUntil(this._destroyCurrSubscription$))
      .subscribe(isConnected => {
        this._isConnected$.next(isConnected);
      });

    // 訂閱帳戶變化
    this._currentWalletService.accounts$
      .pipe(untilDestroyed(this), takeUntil(this._destroyCurrSubscription$))
      .subscribe(accounts => {
        this._accounts$.next(accounts);
      });

    // 訂閱鏈ID變化
    this._currentWalletService.currentChainId$
      .pipe(untilDestroyed(this), takeUntil(this._destroyCurrSubscription$))
      .subscribe(chainId => {
        this._currentChainId$.next(chainId);
      });
  }

  async checkExistingConnection(): Promise<boolean> {
    const currWalletType = this.localStorageService.getConnectedWeb3WalletType();
    if (currWalletType && Object.values(Web3WalletType).includes(currWalletType)) {
      await this._walletServiceInit(currWalletType);
      return true;
    }

    return false; // 未符合時返回 false
  }

  async connectWallet(walletType: Web3WalletType): Promise<void> {
    await this._walletServiceInit(walletType);

    // 實際執行連接
    this._checkWalletServiceExisting();
    return this._currentWalletService!.connectWallet(walletType);
  }

  async disconnectWallet(): Promise<void> {
    this._destroyCurrSubscription$.next();

    // _resetWalletState() 會清掉 localStorage 的連線類型，所以要先取出來。
    const persistedType = this.localStorageService.getConnectedWeb3WalletType();

    this._resetWalletState();

    // localStorage 記錄了連線、但此實例尚未載入過該 adapter 時要補上。
    // 例如 window.TonConnectUiInstance 是跨 window.reBuild() 存活的全域單例，
    // 漏掉會留下斷不掉的殘留連線。登出屬安全相關操作，此處保守處理
    // —— 這是唯一會為了斷線而下載 chunk 的路徑，且僅在確實連過線時發生。
    if (persistedType && !this._loadedAdapters.has(persistedType)) {
      await this._adapter(persistedType);
    }

    // 只斷開曾經載入過的 adapter。
    // 原本是遍歷全部 4 個，那會在斷線時把沒用過的 adapter 一併下載並實例化。
    for (const service of this._loadedAdapters.values()) {
      await service.disconnectWallet();
    }
  }

  updateConnectedStatus(isConnected: boolean): void {
    if (!this._currentWalletService) {
      this._isConnected$.next(isConnected);
    } else {
      this._currentWalletService.updateConnectedStatus(isConnected);
    }
  }

  async signMessage(message: string): Promise<Web3WalletSignatureInfo | null> {
    this._checkWalletServiceExisting();

    // 如果連線後，簽名沒有成功。統一取消連線，確保可以切換錢包後，再次連線簽名
    try {
      const result = await this._currentWalletService!.signMessage(message);
      return result;
    } catch {
      await this.disconnectWallet();
      return null;
    }
  }

  async fetchCurrentChainId(): Promise<number | string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.fetchCurrentChainId();
  }

  async fetchBalance(): Promise<string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.fetchBalance();
  }

  async fetchContractBalance(network: NetworksInterface): Promise<string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.fetchContractBalance(network);
  }

  async sendMainTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.sendMainTransaction(toAddress, amount, network);
  }

  async sendContractTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.sendContractTransaction(toAddress, amount, network);
  }

  async switchNetwork(chainId: number | string | null): Promise<void> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.switchNetwork(chainId);
  }
}
